import { useCallback, useMemo } from 'react';

import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { Blockchain } from '@repo/prsn-blockchain';
import { useMutation } from '@tanstack/react-query';
import { subSeconds } from 'date-fns/subSeconds';
import { NamespaceKeys, useTranslations } from 'next-intl';
import { useBoolean, useUnmount } from 'usehooks-ts';
import { useWaitForTransactionReceipt } from 'wagmi';

import { IPendingBridgeInstance } from '@web/features/staking/BridgeManager';
import { useBridgeStatus } from '@web/features/staking/hooks';
import { BridgeModal } from '@web/features/staking/modals/BridgeModal';
import { getModalStatus } from '@web/features/staking/modals/getModalStatus';
import { bridge } from '@web/features/staking/service';
import { READ_RECEIPT_TIMEOUT } from '@web/features/staking/utils';
import { useCurrentUser } from '@web/features/user/service';
import { ArrowLinkIcon, ErrorIcon, SuccessIcon } from '@web/ui/icons';
import { getStateLayerColor } from '@web/ui/theme/utils';

const MinimizedContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing('1000')};
  border-radius: ${({ theme }) => theme.shape.corner['1000']};
  background: ${({ theme }) => theme.color.surfaceContainerHighest};
  box-shadow: ${({ theme }) => theme.elevation.level1};
  cursor: pointer;
  min-width: 260px;
  z-index: 10;
  transition: all 100ms ease;

  .arrow {
    display: none;
  }

  :hover {
    .arrow {
      display: flex;
    }

    background-image: ${getStateLayerColor('onSurface', 'hover')};
  }

  :active {
    border-radius: ${({ theme }) => theme.shape.corner['1500']};
    background-image: ${getStateLayerColor('onSurface', 'active')};
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
`;

const StatusText = styled.span`
  color: ${({ theme }) => theme.color.onSurfaceVariant};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.default};
`;

const ProcessContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
  color: ${({ theme }) => theme.color.onSurfaceVariant};
`;

const ProcessText = styled.span`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.default};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const LinkIconContainer = styled.div`
  height: ${({ theme }) => theme.size['0625']};

  svg {
    height: 100%;
    width: 100%;
  }
`;

export const BridgePopupMinimized = ({
  pendingBridge,
}: {
  pendingBridge: IPendingBridgeInstance;
}) => {
  const t = useTranslations();
  const { user } = useCurrentUser();
  const {
    value: maximized,
    setTrue: maximize,
    setFalse: minimize,
  } = useBoolean(!pendingBridge.data.txHash);
  const destination =
    pendingBridge.data.source === Blockchain.ETHEREUM
      ? Blockchain.BASE
      : Blockchain.ETHEREUM;

  const bridgeMutation = useMutation({
    mutationFn: bridge,
    meta: {
      notifyError: true,
      notifySuccess: {
        t,
        translationKey: 'BRIDGE_ACTION_SUCCESS',
      },
    },
  });
  const bridgeReceipt = useWaitForTransactionReceipt({
    hash: pendingBridge.data.txHash,
    timeout: READ_RECEIPT_TIMEOUT,
    query: {
      meta: {
        notifyError: true,
      },
    },
  });

  const bridgeStatus = useBridgeStatus({
    destination:
      pendingBridge.data.source === Blockchain.ETHEREUM
        ? Blockchain.BASE
        : Blockchain.ETHEREUM,
    tokenIds: pendingBridge.data.tokenIds,
  });

  const locationStatus = useMemo(() => {
    // We can't decide, lets assume it's success;
    if (!user?.personas?.length) {
      return 'success';
    }
    const serverSyncWithArrivals = user.personas
      .filter((persona) =>
        pendingBridge.data.tokenIds.includes(persona.tokenId),
      )
      .every((persona) => persona.blockchain === destination);

    return serverSyncWithArrivals ? 'success' : 'pending';
  }, [user?.personas, pendingBridge.data.tokenIds, destination]);

  const modalStatus = getModalStatus([
    pendingBridge.data.txHash ? 'success' : bridgeMutation.status,
    bridgeReceipt.status,
    bridgeStatus,
    locationStatus,
  ]);

  const handleClose = useCallback(() => {
    if (modalStatus !== 'pending') {
      pendingBridge.destroy();
    } else {
      minimize();
    }
  }, [minimize, modalStatus, pendingBridge.destroy]);

  const startBridging = useCallback(async () => {
    const txHash = await bridgeMutation.mutateAsync(pendingBridge.data);
    pendingBridge.setHash(txHash);
  }, [bridgeMutation, pendingBridge]);

  const statusIcon = useMemo(() => {
    switch (modalStatus) {
      case 'success':
        return <SuccessIcon size="1250" />;
      case 'error':
        return <ErrorIcon size="1250" />;
      case 'pending':
        return <CircularProgress size={14} />;
      case 'idle':
      default:
        return null;
    }
  }, [modalStatus]);

  const processText = useMemo(() => {
    switch (modalStatus) {
      case 'success':
        return pendingBridge.data.stake
          ? t('BRIDGE_MODAL_MINIMIZED_STAKING')
          : t('BRIDGE_MODAL_MINIMIZED_BRIDGE');
      default:
        return t('BRIDGE_MODAL_MINIMIZED_BRIDGE');
    }
  }, [modalStatus]);

  useUnmount(() => {
    if (
      !pendingBridge.data.txHash &&
      pendingBridge.data.startedAt < subSeconds(new Date(), 5)
    ) {
      pendingBridge.destroy();
    }
  });

  return (
    <>
      {!maximized && (
        <MinimizedContainer onClick={maximize}>
          <ProgressContainer>
            <StatusIcon>{statusIcon}</StatusIcon>
            <StatusText>
              {t(
                `BRIDGE_MODAL_MINIMIZED_STATUS_${modalStatus.toUpperCase()}` as NamespaceKeys<
                  string,
                  string
                >,
              )}
            </StatusText>
          </ProgressContainer>
          <ProcessContainer>
            <ProcessText>{processText}</ProcessText>
            <LinkIconContainer>
              <ArrowLinkIcon className="arrow" />
            </LinkIconContainer>
          </ProcessContainer>
        </MinimizedContainer>
      )}
      <BridgeModal
        personas={pendingBridge.data.personas}
        stake={pendingBridge.data.stake}
        onClose={handleClose}
        open={maximized}
        bridge={startBridging}
        status={modalStatus}
      />
    </>
  );
};
